import { Grid } from '@mui/material';
import { HSpacer, RoutalDialog, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useState } from 'react';

import { CustomFieldDefinition } from '@nx-smartmonkey/shared/domain';
import { dateFormatShortDate } from '@nx-smartmonkey/shared/helpers';
import { FormattedMessage } from 'react-intl';
import { FormArray } from '../../../components/form-fields/FormArray';
import Button from '../../../react-components/Button';
import InputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';
import { ModelToIntlId, TypeToIntlId } from './IntlMapper';
import { ProjectCustomFieldCategoryIcon } from './ProjectCustomFieldIcons';

type ProjectCustomFieldDialogProps = {
  open: boolean;
  currentField: CustomFieldDefinition;
  handleUpdateCustomField: (customFieldDefinition: CustomFieldDefinition) => void;
  onCloseDialog: (event?: any) => void;
};

export const ProjectCustomFieldUpdateDialog = ({
  open,
  currentField,
  handleUpdateCustomField,
  onCloseDialog,
}: ProjectCustomFieldDialogProps) => {
  const [field, setField] = useState<CustomFieldDefinition>(CustomFieldDefinition.create(currentField.props));

  return (
    <RoutalDialog
      id="project-edit-custom-field-dialog"
      open={open}
      dialogProps={{
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
      }}
      onCloseDialog={onCloseDialog}
      titleContent={<Text variant="h6" intlId="projects.settings.fields.custom_fields.update_dialog.title" />}
      footerContent={
        <Grid container justifyContent="flex-end">
          <Button
            disabled={!field.label}
            onClick={() => {
              handleUpdateCustomField(field);
              onCloseDialog();
            }}
            intlId="projects.settings.fields.custom_fields.update_dialog.button"
          />
        </Grid>
      }
    >
      <Grid item container direction="column" spacing={2} alignItems="center">
        <Grid item style={{ width: `100%` }}>
          <div
            style={{
              padding: `20px`,
              borderRadius: `10px`,
              background: RoutalPalette.neutral05,
            }}
          >
            <Text variant="label" intlId="projects.settings.fields.custom_fields.category" />

            <VSpacer small />

            <Grid container direction="row" wrap="nowrap" style={{ width: `100%` }} alignItems="center">
              <ProjectCustomFieldCategoryIcon fieldModel={field.field_model} />
              <HSpacer small />
              <Text
                variant="body"
                style={{ cursor: `pointer`, width: `100%`, textAlign: `start` }}
                intlId={ModelToIntlId[field.field_model]}
              />
            </Grid>

            <VSpacer medium />

            <Text variant="label" intlId="projects.settings.fields.column.id" />

            <VSpacer small />

            <Text variant="body">{field.custom_id}</Text>

            <VSpacer medium />

            <Text variant="label" intlId="projects.settings.fields.column.field_type" />

            <VSpacer small />

            <Text variant="body" intlId={TypeToIntlId[field.field_type]} />

            <VSpacer medium />

            {field.field_type === `categorical` ? (
              <>
                <Text variant="label" intlId="projects.settings.fields.custom_fields.types.categorical" />

                <VSpacer small />

                <FormArray key={`categorical-options`} array={field.options} enabled={false} />

                <VSpacer medium />
              </>
            ) : null}

            <Text variant="label" intlId="projects.settings.fields.column.created_at" />

            <VSpacer small />

            <Text variant="body">{dateFormatShortDate(field.created_at)}</Text>
          </div>

          <VSpacer medium />

          <div
            style={{
              margin: `10px 0`,
              display: `flex`,
              alignItems: `start`,
              flexDirection: `column`,
            }}
          >
            <Text variant="label" intlId="projects.settings.fields.custom_fields.label" />

            <VSpacer small />

            <InputText
              id="field_label"
              fieldValue={field.label}
              placeholderIntlId="projects.settings.fields.custom_fields.label_ph"
              validators={[
                {
                  errorMessage: <FormattedMessage id="projects.settings.fields.custom_fields.label_required" />,
                  isInvalid: (text?: string): boolean => {
                    if (text && text !== ``) {
                      const valueText = text.trim();
                      return valueText.length < 1;
                    }
                    return true;
                  },
                },
              ]}
              onChange={(text: any) => {
                field.label = text ?? ``;
                setField(
                  CustomFieldDefinition.create({
                    ...field.props,
                  })
                );
              }}
              inputContainerStyles={{ width: `100%` }}
            />

            <VSpacer medium />

            <Text variant="label" intlId="projects.settings.fields.custom_fields.description" />

            <VSpacer small />

            <InputText
              id="field_description"
              fieldValue={field.description}
              placeholderIntlId="projects.settings.fields.custom_fields.description_ph"
              onChange={(text: any) => {
                field.description = text ?? ``;
                setField(
                  CustomFieldDefinition.create({
                    ...field.props,
                  })
                );
              }}
              inputContainerStyles={{ width: `100%` }}
            />
          </div>
        </Grid>
      </Grid>
    </RoutalDialog>
  );
};
